import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCalendar,
  faClock,
  faUsers,
  faChartLine,
  faBullseye,
  faRocket,
  faLightbulb,
  faExclamationTriangle,
  faCheckCircle,
  faPlay,
  faEye,
  faHeart,
  faComment,
  faShare,
  faHashtag,
  faImage,
  faVideo,
  faFileText,
  faBullhorn,
  faStar,
  faThumbsUp,
  faCrosshairs,
  faTrophy,
  faArrowTrendUp,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram,
  faFacebook,
  faTwitter,
  faLinkedin,
  faTiktok
} from '@fortawesome/free-brands-svg-icons';
import { useAIRecommendations } from '../../context/AIRecommendationsContext';

const AIRecommendationDetailPage: React.FC = () => {
  const { id, lang } = useParams<{ id: string; lang: string }>();
  const navigate = useNavigate();
  const { state, setRecommendationDetail, fetchRecommendations } = useAIRecommendations();
  const [activeTab, setActiveTab] = useState<'overview' | 'steps' | 'examples' | 'metrics'>('overview');
  const location = useLocation();
  const currentLang = lang || 'tr';
  const resolveSection = (pathname: string) => {
    // Remove language prefix for section detection
    const pathWithoutLang = pathname.replace(/^\/(tr|en)/, '');
    if (pathWithoutLang.startsWith('/web-site')) return 'web-site';
    if (pathWithoutLang.startsWith('/ads')) return 'ads';
    return 'social-media';
  };
  const section = resolveSection(location.pathname);
  const recommendationsPath = `/${currentLang}/${section}/ai-recommendations`;

  // Tüm recommendation'ları tek bir listede topla
  const getAllRecommendations = () => {
    if (!state.recommendations) return [];
    
    let allRecommendations: any[] = [];
    
    // Collect all recommendations from all categories with category info
    Object.entries(state.recommendations).forEach(([categoryKey, categoryData]: [string, any]) => {
      if (categoryData.recommendations) {
        categoryData.recommendations.forEach((rec: any) => {
          allRecommendations.push({ ...rec, _categoryKey: categoryKey });
        });
      }
      // Also include basic recommendations
      if (categoryData.basic) {
        categoryData.basic.forEach((rec: any) => {
          allRecommendations.push({ ...rec, _categoryKey: categoryKey });
        });
      }
    });

    return allRecommendations;
  };

  // Eğer state'te yoksa (sayfa yenilenmişse), recommendation listesinden index ile bul
  useEffect(() => {
    // Önce recommendations yüklenmiş mi kontrol et, yoksa yükle
    if (!state.recommendations && !state.loading.recommendations) {
      fetchRecommendations();
      return;
    }

    // Eğer recommendation detail state'te yoksa ve recommendations yüklendiyse, index ile bul
    if (!state.recommendationDetail && state.recommendations && id) {
      const allRecommendations = getAllRecommendations();
      const index = parseInt(id, 10);
      
      if (!isNaN(index) && index >= 0 && index < allRecommendations.length) {
        const foundRecommendation = allRecommendations[index];
        if (foundRecommendation) {
          // Eğer recommendation bulunduysa, state'e kaydet
          setRecommendationDetail(foundRecommendation);
        }
      }
    }
  }, [id, state.recommendations, state.recommendationDetail, state.loading.recommendations, setRecommendationDetail, fetchRecommendations]);

  // State'ten güncel recommendation detail'i al
  const recommendationDetail = state.recommendationDetail;

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return faInstagram;
      case 'facebook':
        return faFacebook;
      case 'twitter':
        return faTwitter;
      case 'linkedin':
        return faLinkedin;
      case 'tiktok':
        return faTiktok;
      default:
        return faBullhorn;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'yüksek':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'orta':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'düşük':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      default:
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort.toLowerCase()) {
      case 'yüksek':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'orta':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'düşük':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      default:
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    }
  };

  const getPostTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'instagram reels':
      case 'tiktok video':
        return faVideo;
      case 'instagram carousel':
      case 'instagram story':
        return faImage;
      case 'linkedin article':
      case 'facebook live':
        return faFileText;
      case 'twitter poll':
        return faBullhorn;
      default:
        return faImage;
    }
  };

  // Artık API çağrısı yok, sadece state'ten kontrol ediyoruz
  if (!recommendationDetail) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400 text-6xl mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Öneri Bulunamadı</h1>
          <p className="text-white/70 mb-8 max-w-md">Öneri detayları yüklenemedi. Lütfen öneriler sayfasına dönüp tekrar deneyin.</p>
          <button 
            onClick={() => navigate(recommendationsPath)}
            className="bg-gradient-to-r from-purple-500 to-blue-500 px-8 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity"
          >
            Öneriler Sayfasına Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(recommendationsPath)}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-white/70 hover:text-white" />
          </button>
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={getPlatformIcon(recommendationDetail.platform)} className="text-4xl text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">{recommendationDetail.title}</h1>
              <p className="text-white/70">{recommendationDetail.platform} • {recommendationDetail.category}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(recommendationDetail.priority)}`}>
            {recommendationDetail.priority} Öncelik
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getEffortColor(recommendationDetail.effort)}`}>
            {recommendationDetail.effort} Çaba
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Açıklama</h2>
        <p className="text-white/70 leading-relaxed">{recommendationDetail.description}</p>
      </div>

      {/* Key Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FontAwesomeIcon icon={faRocket} className="text-green-400 text-xl" />
            <h3 className="text-lg font-semibold text-white">Etki</h3>
          </div>
          <p className="text-white/70">{recommendationDetail.impact}</p>
        </div>

        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FontAwesomeIcon icon={faClock} className="text-blue-400 text-xl" />
            <h3 className="text-lg font-semibold text-white">Süre</h3>
          </div>
          <p className="text-white/70">{recommendationDetail.timeline}</p>
        </div>

      </div>

      {/* Tabs */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex space-x-1 mb-6 bg-white/5 rounded-lg p-1">
          {[
            { id: 'overview', label: 'Genel Bakış', icon: faEye },
            { id: 'steps', label: 'Adımlar', icon: faCheckCircle },
            { id: 'examples', label: 'Örnekler', icon: faImage },
            { id: 'metrics', label: 'Metrikler', icon: faChartLine }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Öneri Detayları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faCrosshairs} className="text-blue-400" />
                    <span className="text-white font-medium">Kategori</span>
                  </div>
                  <p className="text-white/70">{recommendationDetail.category}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faBullseye} className="text-green-400" />
                    <span className="text-white font-medium">Platform</span>
                  </div>
                  <p className="text-white/70">{recommendationDetail.platform}</p>
                </div>
              </div>
            </div>

            {recommendationDetail.tips && recommendationDetail.tips.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">İpuçları</h3>
                <div className="space-y-2">
                  {recommendationDetail.tips.map((tip, index) => (
                    <div key={index} className="flex items-start space-x-3 bg-white/5 rounded-lg p-4">
                      <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                      <p className="text-white/70">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'steps' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Uygulama Adımları</h3>
            <div className="space-y-4">
              {recommendationDetail.steps.map((step, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-2">{step.title}</h4>
                      <p className="text-white/70 mb-2">{step.description}</p>
                      <div className="flex items-center space-x-2 text-white/50 text-sm">
                        <FontAwesomeIcon icon={faClock} />
                        <span>Tahmini süre: {step.estimatedTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">İçerik Örnekleri</h3>
            <div className="space-y-4">
              {recommendationDetail.postExamples.map((example, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <FontAwesomeIcon icon={getPostTypeIcon(example.type)} className="text-blue-400 text-xl mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-white font-semibold">{example.title}</h4>
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                          {example.type}
                        </span>
                      </div>
                      <p className="text-white/70 mb-3">{example.description}</p>
                      
                      <div className="bg-white/5 rounded-lg p-3 mb-3">
                        <p className="text-white/80 font-medium mb-2">Caption:</p>
                        <p className="text-white/70 italic">"{example.caption}"</p>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {example.hashtags.map((hashtag, hashtagIndex) => (
                          <span key={hashtagIndex} className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                            {hashtag}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-white/70">
                          <FontAwesomeIcon icon={faClock} />
                          <span>Optimal zaman: {example.optimalTime}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-white/70">
                          <FontAwesomeIcon icon={faArrowTrendUp} />
                          <span>Beklenen etkileşim: {example.expectedEngagement}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Hedef Metrikler</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faHeart} className="text-red-400" />
                  <span className="text-white font-medium">Etkileşim</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Mevcut:</span>
                    <span className="text-white">{recommendationDetail.metrics.currentEngagement}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Hedef:</span>
                    <span className="text-green-400">{recommendationDetail.metrics.targetEngagement}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faEye} className="text-blue-400" />
                  <span className="text-white font-medium">Erişim</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Mevcut:</span>
                    <span className="text-white">{recommendationDetail.metrics.currentReach.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Hedef:</span>
                    <span className="text-green-400">{recommendationDetail.metrics.targetReach.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FontAwesomeIcon icon={faUsers} className="text-purple-400" />
                  <span className="text-white font-medium">Takipçi</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Mevcut:</span>
                    <span className="text-white">{recommendationDetail.metrics.currentFollowers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Hedef:</span>
                    <span className="text-green-400">{recommendationDetail.metrics.targetFollowers.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIRecommendationDetailPage;
